/**
 * The authoring document: what gets stored, and what the canvas is rebuilt from.
 *
 * ## Why this is not `QuestionnaireDefinition`
 *
 * `QuestionnaireDefinition` is the shape inherited from the engine's classpath JSON, and it
 * carries that format's defects: an edge is an index into the source node's rule array, so it
 * has no identity; evaluation order is array position, so appending a `true` catch-all makes
 * every later rule dead with nothing in the data saying so; conditions are raw JS strings,
 * which is how three live defects got in; `scale` stores a ratio while rules compare against
 * the displayed percentage, so `Q_1_1_10 >= 30` can never fire.
 *
 * This document fixes those by construction, and the engine's format becomes a COMPILED
 * output rather than the thing being edited. See `toEngine` for that direction (not yet
 * written: the engine still reads its classpath files).
 *
 * ## Why layout lives in here
 *
 * The canvas has to redraw exactly after a reload, and positions are the only thing the
 * semantics do not already determine (edges are derived from transitions, so they need no
 * storage). Keeping them in the same document makes a save atomic — there is no state where
 * the questions are v8 and the layout is v5.
 *
 * They are a SEPARATE SECTION, though, and conflict detection ignores it: dragging a card
 * must not count as a change to the tree, or the history fills with noise and two people each
 * nudging a card conflict on every save over something neither of them cares about.
 */
import type { AnswerOption, QuestionnaireDefinition, QuestionnaireNode } from '../types';
import { type FlatCondition, type Term as FlatTerm, toFlat } from './condition';
import { type GraphFacts, type QuestionKind, graphFactsOf, questionKindOf } from './projection';

export const SCHEMA_VERSION = 1;

/* ── The document ───────────────────────────────────────────────────────────── */

export type DocQuestionKind = 'SINGLE_SELECT' | 'MULTI_SELECT' | 'NUMBER' | 'TEXT' | 'COMPONENT';

export interface DocText {
  en: string;
  zh?: string;
}

export interface DocOption {
  /** The id AND the stored answer — one identifier, not three. */
  optionValue: string;
  label: DocText;
}

export interface DocNumberFormat {
  unit: string;
  decimals?: number;
}

/** `question` | `fallback` | `sum` | `product`, exactly one key set. */
export type DocOperand =
  | { question: string }
  | { fallback: DocOperand[] }
  | { sum: DocOperand[] }
  | { product: DocOperand[] };

export type DocCompareOperator =
  | 'IS'
  | 'CONTAINS'
  | 'EQUALS'
  | 'GREATER_THAN'
  | 'GREATER_OR_EQUAL'
  | 'LESS_THAN'
  | 'LESS_OR_EQUAL';

/** A union: exactly one key set. */
export type DocTerm =
  | { compare: { left: DocOperand; operator: DocCompareOperator; value: string | number } }
  | { answered: { questionId: string } }
  | { granted: { programName: string; granted: boolean } };

export interface DocCondition {
  join: 'ALL' | 'ANY';
  terms: DocTerm[];
}

export interface DocEligibilityRule {
  ruleId: string;
  condition: DocCondition;
  programs: string[];
}

export interface DocQuestion {
  questionId: string;
  kind: DocQuestionKind;
  prompt: DocText;
  note?: DocText;
  options?: DocOption[];
  numberFormat?: DocNumberFormat;
  component?: string;
  semantic?: string;
  eligibility?: DocEligibilityRule[];
}

export interface DocTransition {
  transitionId: string;
  fromQuestionId: string;
  toQuestionId: string;
  /** Lower runs first. Data, not array position. */
  priority: number;
  /** Absent = unconditional. */
  condition?: DocCondition;
}

export interface DecisionTreeDocument {
  schemaVersion: number;
  entryQuestionId: string;
  questions: DocQuestion[];
  transitions: DocTransition[];
  layout: { positions: Record<string, { x: number; y: number }> };
}

/* ── Conversion ─────────────────────────────────────────────────────────────── */

const KIND: Record<QuestionKind, DocQuestionKind> = {
  single: 'SINGLE_SELECT',
  multi: 'MULTI_SELECT',
  number: 'NUMBER',
  text: 'TEXT',
  custom: 'COMPONENT',
};

const OPERATOR: Record<FlatTerm['op'], DocCompareOperator> = {
  is: 'IS',
  contains: 'CONTAINS',
  '===': 'EQUALS',
  '>': 'GREATER_THAN',
  '>=': 'GREATER_OR_EQUAL',
  '<': 'LESS_THAN',
  '<=': 'LESS_OR_EQUAL',
};

/**
 * `{ en, zh? }`, dropping the legacy `id`.
 *
 * The old `MultiLanguageText` had three fields, and in all 30 live nodes `id` was a verbatim
 * copy of `en` — pure duplication, and a third place for the same string to drift.
 */
function text(t: { en: string; cn?: string } | undefined): DocText | undefined {
  if (!t) return undefined;
  if (!t.en && !t.cn) return undefined;
  return t.cn ? { en: t.en, zh: t.cn } : { en: t.en };
}

/**
 * Strip a leading `a) ` from an option label.
 *
 * The identifier belongs to `optionValue` alone. The live labels repeat it as a leading
 * letter, and on `Q1.1.3.1` the two disagreed — value `f` labelled `e) R-PPA` — which forced
 * the BD frontend to validate one question by its label's letter and its structurally
 * identical sibling by value, so "R-PPA" could combine on one and not the other. Removing the
 * letter removes the thing there was to drift from.
 */
export function stripOptionLetter(label: string): string {
  return label.replace(/^\s*[A-Za-z0-9]\)\s*/, '');
}

function option(o: AnswerOption): DocOption {
  const en = stripOptionLetter(o.optionText.en ?? '');
  const zh = o.optionText.cn ? stripOptionLetter(o.optionText.cn) : undefined;
  return { optionValue: o.value, label: zh ? { en, zh } : { en } };
}

/** A flat condition from `condition.ts`, in document form. */
function condition(flat: FlatCondition, refToId: (ref: string) => string): DocCondition {
  return {
    join: flat.join === 'and' ? 'ALL' : 'ANY',
    terms: flat.terms.map((t) => ({
      compare: {
        left: { question: refToId(t.ref) },
        operator: OPERATOR[t.op],
        value: t.value,
      },
    })),
  };
}

export interface ToDocumentResult {
  document: DecisionTreeDocument;
  /**
   * Expressions that could not be structured, by owner.
   *
   * NOT silently dropped and NOT approximated. Measured over the live tree: 0 of 32
   * transitions and 16 of 30 eligibility rules land here — the four shapes the flat model has
   * no word for yet (arithmetic over answers, fallback chains, `hasAnyMatch` over the full
   * option set, `hasKey`). Until the document gains those term shapes, a save that silently
   * dropped them would lose half the eligibility logic, so the caller has to decide.
   */
  unconvertible: { ownerId: string; kind: 'transition' | 'eligibility'; index: number; expression: string }[];
}

/**
 * Build the document from the canvas's current state.
 *
 * `positions` comes from the layout store rather than the definition, because that is where
 * the canvas keeps it — see `layoutStorage`.
 */
export function toDocument(
  definition: QuestionnaireDefinition,
  positions: Record<string, { x: number; y: number }>,
  facts: GraphFacts = graphFactsOf(definition),
): ToDocumentResult {
  const refToId = (ref: string) =>
    facts.byQualifiedId.get(ref)?.nodeId ?? definition.nodes.find((n) => n.qualifiedNodeId === ref)?.nodeId ?? ref;

  const unconvertible: ToDocumentResult['unconvertible'] = [];
  const parse = (expression: string): DocCondition | undefined => {
    const flat = toFlat(expression, definition, facts);
    return flat ? condition(flat, refToId) : undefined;
  };

  const questions: DocQuestion[] = [];
  const transitions: DocTransition[] = [];

  for (const n of definition.nodes as QuestionnaireNode[]) {
    const kind = questionKindOf(n);
    const q: DocQuestion = { questionId: n.nodeId, kind: KIND[kind], prompt: text(n.questionText)! };

    const note = text(n.note);
    if (note) q.note = note;
    if (kind === 'single' || kind === 'multi') q.options = (n.answerOptions ?? []).map(option);
    if (kind === 'number') {
      // `unit` only. No scale factor: the stored number is the number the author typed in the
      // stated unit, so a condition compares against that same number. The old model stored
      // Q_1_1_10 as a ratio and displayed a percentage, and its rule read `>= 30` against a
      // value never above 1 — unfireable, and invisible to every checker.
      q.numberFormat = { unit: n.unit ?? '$K' };
    }
    if (n.uiComponent) q.component = n.uiComponent;

    const eligibility: DocEligibilityRule[] = [];
    (n.programRules ?? []).forEach((r, i) => {
      const c = parse(r.expression);
      if (!c) {
        unconvertible.push({ ownerId: n.nodeId, kind: 'eligibility', index: i, expression: r.expression });
        return;
      }
      eligibility.push({ ruleId: `${n.nodeId}-e${i + 1}`, condition: c, programs: r.values });
    });
    if (eligibility.length) q.eligibility = eligibility;

    questions.push(q);

    (n.nextQuestionRules ?? []).forEach((r, i) => {
      const isDefault = r.expression.trim() === 'true';
      const c = isDefault ? undefined : parse(r.expression);
      if (!isDefault && !c) {
        unconvertible.push({ ownerId: n.nodeId, kind: 'transition', index: i, expression: r.expression });
        /*
         * Dropped, NOT emitted without its condition.
         *
         * `condition` is omitted when it is absent, and a transition with no condition is
         * unconditional. So falling through here turned an unparseable CONDITIONAL rule into
         * an always-take edge — which, under first-match-by-priority, also shadows every
         * lower-priority sibling. That is not a lossy conversion, it is a silent rewiring of
         * the tree into a shape its author never wrote, and it contradicts this file's own
         * contract two paragraphs up ("NOT silently dropped and NOT approximated").
         *
         * Dropping leaves the node short an exit, which `computeFlow` reports as
         * `blocked / no-branch` and the save dialog lists by expression. Visibly stuck beats
         * quietly wrong.
         *
         * Latent on today's data — 0 of 32 transitions are unconvertible — and reachable the
         * moment one uses arithmetic, a fallback chain, full-option `hasAnyMatch`, or `hasKey`.
         */
        return;
      }
      transitions.push({
        transitionId: `${n.nodeId}->${r.nextQuestionId}#${i + 1}`,
        fromQuestionId: n.nodeId,
        toQuestionId: r.nextQuestionId,
        // 1-based and explicit. The old model left this implicit in array order, which is why
        // appending a catch-all silently killed every rule after it.
        priority: i + 1,
        ...(c ? { condition: c } : {}),
      });
    });

    if (n.nextNodeId) {
      transitions.push({
        transitionId: `${n.nodeId}->${n.nextNodeId}`,
        fromQuestionId: n.nodeId,
        toQuestionId: n.nextNodeId,
        priority: 1,
      });
    }
  }

  const entry = definition.nodes.find((n) => n.isFirstNode) ?? definition.nodes[0];

  return {
    document: {
      schemaVersion: SCHEMA_VERSION,
      // A pointer, not a boolean on a node: `isFirstNode` can disagree with the graph, and
      // two nodes can both claim it.
      entryQuestionId: entry?.nodeId ?? '',
      questions,
      transitions,
      layout: { positions },
    },
    unconvertible,
  };
}

/* ── Bytes ──────────────────────────────────────────────────────────────────── */

/**
 * The exact bytes sent and stored.
 *
 * Key order is whatever the builders above produce, and that is fine BECAUSE this function is
 * the only thing that serialises: the hash the commit sends and the bytes the service stores
 * are the same string. What must never happen is a second serialiser with a different key
 * order, because then the hash would not match its own document.
 */
export const serializeDocument = (doc: DecisionTreeDocument): string => JSON.stringify(doc);

/** SHA-256 hex of the serialised document, for `CommitDecisionTreeVersion.documentSha256`. */
export async function documentSha256(json: string): Promise<string> {
  const bytes = new TextEncoder().encode(json);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
